import { uniq, uniqWith } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { isTruss } from '@paragon/contract-library';
import { getPlateTypeString, nominalSizeMatches } from '@paragon/material-lists';
import { notNullish } from '@paragon/utilities';

import { RootState } from '..';
import { getActiveComponentDesignQuantity } from './componentDesignQuantitySelectors';
import { getActiveComponentDesign } from './getActiveComponentDesignSelector';
import { getAnalysisSettingsForActiveComponentDesign } from './getAnalysisSettingsForActiveComponentDesign';
import { getPriceForActiveComponentDesign } from './stockLengthAndPriceSelectors';

export const getComponentLabelInformation = createSelector(
  getActiveComponentDesign,
  getActiveComponentDesignQuantity,
  (state: RootState) => state.plateProperties.plateTypeProperties,
  getAnalysisSettingsForActiveComponentDesign,
  getPriceForActiveComponentDesign,

  (componentDesign, quantity, plateTypeProperties, analysisSettings, priceInformation) => {
    if (!componentDesign) return null;

    const {
      guid: componentDesignGuid,
      component: { members, name: componentName },
    } = componentDesign;

    const topChordLumbers = uniqWith(
      members.filter((m) => m.memberType === 'TopChord').map((m) => m.lumber),
      nominalSizeMatches,
    );
    const bottomChordLumbers = uniqWith(
      members.filter((m) => m.memberType === 'BottomChord').map((m) => m.lumber),
      nominalSizeMatches,
    );
    const numberOfPlies = isTruss(componentDesign.component) ? componentDesign.component.numberOfPlies : 1;
    const multiPlyConnectionType = analysisSettings?.multiPlyConnectionType;

    const plateManufacturers = uniq(
      componentDesign.component.platePairs
        .map(
          (platePair) =>
            plateTypeProperties[getPlateTypeString(platePair.plateType, platePair.tangiblePlateTypesForComboPlateType)]
              ?.plateManufacturer,
        )
        .filter(notNullish),
    );

    return {
      componentName,
      componentDesignGuid,
      quantity,
      topChordLumbers,
      bottomChordLumbers,
      thickness: isTruss(componentDesign.component) ? componentDesign.component.thickness : undefined,
      numberOfPlies,
      multiPlyConnectionType,
      plateManufacturers,
      priceInformation,
    };
  },
);
